import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import cn from 'classnames';

import styles from 'css-module/components/ui/_TextareaV2.module.scss';

type TextareaV2Props = ComponentProps<'textarea'> & {
  autoResize?: boolean;
  autoResizeMaxRows?: number;
  isBordered?: boolean;
};

export interface TextareaV2Ref {
  getTextarea: () => HTMLTextAreaElement | null;
  getValue: () => string;
  focus: () => void;
  getSelectionRange: () => { start: number; end: number };
  setValue: (value: string, viaTextEvent?: boolean) => void;
  setCursorPosition: (position: number) => void;
  setSelectionRange: (
    start: number,
    end: number,
    direction?: 'forward' | 'backward' | 'none'
  ) => void;
  insertText: (text: string) => void;
}

const TextareaV2 = forwardRef<TextareaV2Ref, TextareaV2Props>(function TextareaV2(
  { className, rows = 1, autoResize, autoResizeMaxRows, isBordered = true, ...props },
  ref
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea || !autoResize) {
      return;
    }

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;

    if (autoResizeMaxRows) {
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight =
        Number.parseInt(computedStyle.lineHeight) || Number.parseInt(computedStyle.fontSize) * 1.2;
      const maxHeight = lineHeight * autoResizeMaxRows;
      const finalHeight = Math.min(scrollHeight, maxHeight);

      textarea.style.height = `${finalHeight}px`;
      textarea.style.maxHeight = `${maxHeight}px`;
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    } else {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.maxHeight = 'none';
      textarea.style.overflowY = 'hidden';
    }
  }, [autoResize, autoResizeMaxRows]);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [resizeTextarea, props.value, props.defaultValue]);

  useImperativeHandle(ref, () => ({
    getTextarea: () => textareaRef.current,
    getValue: () => textareaRef.current?.value || '',
    focus: () => textareaRef.current?.focus(),
    getSelectionRange: () => ({
      start: textareaRef.current?.selectionStart || 0,
      end: textareaRef.current?.selectionEnd || 0,
    }),
    setValue: (value: string) => {
      if (textareaRef.current) {
        textareaRef.current.value = value;
      }
    },
    setCursorPosition: (position: number) => {
      textareaRef.current?.setSelectionRange(position, position);
    },
    setSelectionRange: (
      start: number,
      end: number,
      direction: 'forward' | 'backward' | 'none' = 'none'
    ) => {
      textareaRef.current?.setSelectionRange(start, end, direction);
    },
    insertText: (text: string) => {
      if (document.execCommand('insertText', false, text)) {
        return;
      }

      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      const value = textarea.value;
      const before = value.substring(0, selectionStart);
      const after = value.substring(selectionEnd);

      textarea.value = before + text + after;
      textarea.setSelectionRange(selectionStart + text.length, selectionStart + text.length);
      resizeTextarea();
    },
  }));

  return (
    <textarea
      ref={textareaRef}
      className={cn(
        styles.textarea,
        {
          [styles.bordered]: isBordered,
        },
        className
      )}
      rows={rows}
      {...props}
      onChange={(e) => {
        props.onChange?.(e);
        resizeTextarea();
      }}
    />
  );
});

export default TextareaV2;
